/**
 * XP-P203A BLE GATT tuning — saved per browser for smartphone / bridge compatibility.
 */
import type { BleBridgeProfile } from './bleBridgeProfile';
import { getPlatform } from './platform';
import {
  ISSC_BLE_CHUNK_BYTES,
  ISSC_BLE_DELAY_ONLY_CHUNK_MS,
  ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
  ISSC_BLE_INTER_CHUNK_DELAY_MS,
  ISSC_BLE_POST_WRITE_SETTLE_MS,
  ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  XP_P203A_BLE_CHUNK_BYTES,
  XP_P203A_BLE_INTER_CHUNK_DELAY_MS,
  XP_P203A_BLE_POST_WRITE_SETTLE_MS,
} from './xpP203aBleConstants';

const LS_KEY = 'zeettu.xpP203a.bleSettings';

/** Default ATT payload before MTU exchange on many Android centrals. */
export const XP_P203A_BLE_ATT_CHUNK_CAP = 20;

export type XpP203aBleProfileTiming = {
  chunkBytes: number;
  interChunkDelayMs: number;
  postWriteSettleMs: number;
  isscDelayOnlyChunkMs: number;
  isscDelayOnlyTsplChunkMs: number;
  isscTsplInterChunkMs: number;
};

export type XpP203aBleSettings = {
  /**
   * Mobile-friendly pacing: conservative chunks/delays and per-bridge memory.
   * Defaults on for Android; saved when user toggles.
   */
  smartAccessibility: boolean;
  /** Split TSPL at CRLF before BLE writes (recommended on phones). */
  tsplLineChunking: boolean;
  /** Optional global timing overrides (advanced). */
  chunkBytes?: number;
  interChunkDelayMs?: number;
  postWriteSettleMs?: number;
  /** ISSC-only: use writeValue (with response) instead of writeWithoutResponse. */
  isscWriteWithResponse?: boolean;
  /** Last GATT bridge that completed a print successfully. */
  lastSuccessfulBridgeProfile?: BleBridgeProfile;
  /** Learned / user-tuned timing per bridge profile. */
  bridgeProfiles?: Partial<Record<BleBridgeProfile, Partial<XpP203aBleProfileTiming>>>;
};

const BRIDGE_BASE: Record<BleBridgeProfile, XpP203aBleProfileTiming> = {
  issc: {
    chunkBytes: ISSC_BLE_CHUNK_BYTES,
    interChunkDelayMs: ISSC_BLE_INTER_CHUNK_DELAY_MS,
    postWriteSettleMs: ISSC_BLE_POST_WRITE_SETTLE_MS,
    isscDelayOnlyChunkMs: ISSC_BLE_DELAY_ONLY_CHUNK_MS,
    isscDelayOnlyTsplChunkMs: ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
    isscTsplInterChunkMs: ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  },
  nus: {
    chunkBytes: XP_P203A_BLE_CHUNK_BYTES,
    interChunkDelayMs: XP_P203A_BLE_INTER_CHUNK_DELAY_MS,
    postWriteSettleMs: XP_P203A_BLE_POST_WRITE_SETTLE_MS,
    isscDelayOnlyChunkMs: ISSC_BLE_DELAY_ONLY_CHUNK_MS,
    isscDelayOnlyTsplChunkMs: ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
    isscTsplInterChunkMs: ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  },
  vendor: {
    chunkBytes: XP_P203A_BLE_CHUNK_BYTES,
    interChunkDelayMs: XP_P203A_BLE_INTER_CHUNK_DELAY_MS,
    postWriteSettleMs: XP_P203A_BLE_POST_WRITE_SETTLE_MS,
    isscDelayOnlyChunkMs: ISSC_BLE_DELAY_ONLY_CHUNK_MS,
    isscDelayOnlyTsplChunkMs: ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
    isscTsplInterChunkMs: ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  },
  generic: {
    chunkBytes: XP_P203A_BLE_CHUNK_BYTES,
    interChunkDelayMs: XP_P203A_BLE_INTER_CHUNK_DELAY_MS,
    postWriteSettleMs: XP_P203A_BLE_POST_WRITE_SETTLE_MS,
    isscDelayOnlyChunkMs: ISSC_BLE_DELAY_ONLY_CHUNK_MS,
    isscDelayOnlyTsplChunkMs: ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
    isscTsplInterChunkMs: ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  },
};

/** Extra headroom when smart accessibility is on (typical Android BLE stacks). */
const SMART_MOBILE_BOOST: Partial<XpP203aBleProfileTiming> = {
  interChunkDelayMs: 65,
  postWriteSettleMs: 360,
  isscDelayOnlyChunkMs: 50,
  isscDelayOnlyTsplChunkMs: 75,
  isscTsplInterChunkMs: 18,
};

type BleSettingsListener = () => void;
const listeners = new Set<BleSettingsListener>();

export function subscribeXpP203aBleSettings(listener: BleSettingsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyBleSettingsChange(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

function isMobilePlatform(): boolean {
  const p = getPlatform();
  return p === 'android' || p === 'ios';
}

export function getDefaultXpP203aBleSettings(): XpP203aBleSettings {
  return {
    smartAccessibility: isMobilePlatform(),
    tsplLineChunking: true,
  };
}

function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.floor(n)));
}

function readStored(): XpP203aBleSettings {
  const defaults = getDefaultXpP203aBleSettings();
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return defaults;
  }
  try {
    const raw = globalThis.localStorage.getItem(LS_KEY);
    if (!raw?.trim()) return defaults;
    const j = JSON.parse(raw) as Partial<XpP203aBleSettings>;
    return {
      smartAccessibility: typeof j.smartAccessibility === 'boolean' ? j.smartAccessibility : defaults.smartAccessibility,
      tsplLineChunking: typeof j.tsplLineChunking === 'boolean' ? j.tsplLineChunking : defaults.tsplLineChunking,
      chunkBytes: typeof j.chunkBytes === 'number' ? j.chunkBytes : undefined,
      interChunkDelayMs: typeof j.interChunkDelayMs === 'number' ? j.interChunkDelayMs : undefined,
      postWriteSettleMs: typeof j.postWriteSettleMs === 'number' ? j.postWriteSettleMs : undefined,
      isscWriteWithResponse: typeof j.isscWriteWithResponse === 'boolean' ? j.isscWriteWithResponse : undefined,
      lastSuccessfulBridgeProfile:
        j.lastSuccessfulBridgeProfile === 'issc' ||
        j.lastSuccessfulBridgeProfile === 'nus' ||
        j.lastSuccessfulBridgeProfile === 'vendor' ||
        j.lastSuccessfulBridgeProfile === 'generic'
          ? j.lastSuccessfulBridgeProfile
          : undefined,
      bridgeProfiles: j.bridgeProfiles ?? undefined,
    };
  } catch {
    return defaults;
  }
}

export function getXpP203aBleSettings(): XpP203aBleSettings {
  return readStored();
}

export function saveXpP203aBleSettings(next: XpP203aBleSettings): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.setItem(LS_KEY, JSON.stringify(next));
    notifyBleSettingsChange();
  } catch {
    /* quota */
  }
}

export function clearXpP203aBleSettings(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.removeItem(LS_KEY);
    notifyBleSettingsChange();
  } catch {
    /* ignore */
  }
}

function mergeTiming(
  base: XpP203aBleProfileTiming,
  patch?: Partial<XpP203aBleProfileTiming>,
): XpP203aBleProfileTiming {
  if (!patch) return base;
  return {
    chunkBytes: patch.chunkBytes ?? base.chunkBytes,
    interChunkDelayMs: patch.interChunkDelayMs ?? base.interChunkDelayMs,
    postWriteSettleMs: patch.postWriteSettleMs ?? base.postWriteSettleMs,
    isscDelayOnlyChunkMs: patch.isscDelayOnlyChunkMs ?? base.isscDelayOnlyChunkMs,
    isscDelayOnlyTsplChunkMs: patch.isscDelayOnlyTsplChunkMs ?? base.isscDelayOnlyTsplChunkMs,
    isscTsplInterChunkMs: patch.isscTsplInterChunkMs ?? base.isscTsplInterChunkMs,
  };
}

function applySmartBoost(timing: XpP203aBleProfileTiming, smart: boolean): XpP203aBleProfileTiming {
  if (!smart || !isMobilePlatform()) return timing;
  const boost = SMART_MOBILE_BOOST;
  return {
    ...timing,
    interChunkDelayMs: Math.max(timing.interChunkDelayMs, boost.interChunkDelayMs ?? 0),
    postWriteSettleMs: Math.max(timing.postWriteSettleMs, boost.postWriteSettleMs ?? 0),
    isscDelayOnlyChunkMs: Math.max(timing.isscDelayOnlyChunkMs, boost.isscDelayOnlyChunkMs ?? 0),
    isscDelayOnlyTsplChunkMs: Math.max(timing.isscDelayOnlyTsplChunkMs, boost.isscDelayOnlyTsplChunkMs ?? 0),
    isscTsplInterChunkMs: Math.max(timing.isscTsplInterChunkMs, boost.isscTsplInterChunkMs ?? 0),
  };
}

function applyGlobalOverrides(
  timing: XpP203aBleProfileTiming,
  settings: XpP203aBleSettings,
): XpP203aBleProfileTiming {
  return mergeTiming(timing, {
    chunkBytes: settings.chunkBytes,
    interChunkDelayMs: settings.interChunkDelayMs,
    postWriteSettleMs: settings.postWriteSettleMs,
  });
}

/**
 * Effective BLE write pacing for XP-P203A on the active GATT bridge.
 */
export function resolveXpP203aBleTiming(
  bridgeProfile: BleBridgeProfile,
  opts?: { smartAccessibility?: boolean },
): XpP203aBleProfileTiming {
  const settings = readStored();
  const smart = opts?.smartAccessibility ?? settings.smartAccessibility;
  const learned = settings.bridgeProfiles?.[bridgeProfile];
  let timing = mergeTiming(BRIDGE_BASE[bridgeProfile], learned);
  timing = applySmartBoost(timing, smart);
  timing = applyGlobalOverrides(timing, settings);
  timing = {
    ...timing,
    chunkBytes: clampInt(timing.chunkBytes, 8, XP_P203A_BLE_ATT_CHUNK_CAP),
    interChunkDelayMs: clampInt(timing.interChunkDelayMs, 0, 150),
    postWriteSettleMs: clampInt(timing.postWriteSettleMs, 0, 2500),
    isscDelayOnlyChunkMs: clampInt(timing.isscDelayOnlyChunkMs, 0, 200),
    isscDelayOnlyTsplChunkMs: clampInt(timing.isscDelayOnlyTsplChunkMs, 0, 200),
    isscTsplInterChunkMs: clampInt(timing.isscTsplInterChunkMs, 0, 200),
  };
  return timing;
}

export function shouldXpP203aTsplLineChunking(): boolean {
  return readStored().tsplLineChunking !== false;
}

export function shouldXpP203aIsscWriteWithResponse(): boolean {
  return readStored().isscWriteWithResponse === true;
}

/** Global BLE link fields for printerModelConfig / connect. */
export function getXpP203aBleLinkDefaults(): {
  bleChunkBytes: number;
  bleInterChunkDelayMs: number;
  blePostWriteSettleMs: number;
  bleWriteWithResponse: boolean;
} {
  const settings = readStored();
  const profile = settings.lastSuccessfulBridgeProfile ?? 'generic';
  const timing = resolveXpP203aBleTiming(profile, { smartAccessibility: settings.smartAccessibility });
  return {
    bleChunkBytes: timing.chunkBytes,
    bleInterChunkDelayMs: timing.interChunkDelayMs,
    blePostWriteSettleMs: timing.postWriteSettleMs,
    bleWriteWithResponse: false,
  };
}

/** Remember successful print timing for this bridge (smart accessibility learning). */
export function recordXpP203aBlePrintSuccess(
  bridgeProfile: BleBridgeProfile,
  timing: XpP203aBleProfileTiming,
): void {
  const current = readStored();
  const bridgeProfiles = { ...(current.bridgeProfiles ?? {}) };
  bridgeProfiles[bridgeProfile] = { ...timing };
  saveXpP203aBleSettings({
    ...current,
    lastSuccessfulBridgeProfile: bridgeProfile,
    bridgeProfiles,
  });
}

/** ATT payload cap before MTU exchange (20 bytes on many Android centrals). */
export function getXpP203aAttChunkCap(useWithoutResponse: boolean): number {
  if (!useWithoutResponse) return 244;
  return XP_P203A_BLE_ATT_CHUNK_CAP;
}
